import { inject, Injectable } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import {
  ApiError,
  LoginCredentials,
  TokenLoginResponse,
  User,
} from '@seatix/shared/domain';
import { catchError, map, Observable, tap } from 'rxjs';
import { AuthApiService } from '../auth-api/auth-api.service';
import { AuthTokenStorageService } from '../services/auth-token-storage.service';
import { AuthProvider } from './auth.provider';

@Injectable()
export class TokenAuthProvider implements AuthProvider {
  clearAuth(): void {
    throw new Error('Method not implemented.');
  }
  readonly authApiService = inject(AuthApiService);
  readonly #router = inject(Router);
  readonly #tokenStorage = inject(AuthTokenStorageService);

  login(
    credentials: LoginCredentials,
    _turnstileToken?: string,
  ): Observable<TokenLoginResponse> {
    return this.authApiService.loginWithToken(credentials, _turnstileToken);
  }

  logout(): Observable<void> {
    return this.authApiService.logout().pipe(
      tap(() => {
        this.#tokenStorage.clear();
      }),
      map(() => void 0),
    );
  }

  check(): Observable<{ authenticated: boolean }> {
    return toObservable(this.#tokenStorage.hasActiveToken).pipe(
      map((hasToken) => ({
        authenticated: hasToken,
      })),
    );
  }

  getIdentity(): Observable<User> {
    return this.authApiService.getUserData().pipe(
      map((res) => res.data),
      catchError((error) => {
        this.#tokenStorage.clear();
        throw new Error(error);
      }),
    );
  }

  fetchAndStoreCurrentUser(): Observable<User> {
    return this.authApiService.getUserData().pipe(map((res) => res.data));
  }

  register(): Observable<never> {
    throw new Error('Registration not available with token auth');
  }

  refreshToken(): Observable<never> {
    throw new Error('Token refresh not implemented');
  }

  resetPassword(): Observable<never> {
    throw new Error('Password reset not available with token auth');
  }

  verifyEmail(): Observable<never> {
    throw new Error('Email verification not available with token auth');
  }

  onLoginSuccess(_response: TokenLoginResponse): void {
    this.#tokenStorage.setToken(_response.data.token);
    this.#router.navigate(['/']);
  }

  onLoginFailure(_error: ApiError): void {
    // Silent failure - errors handled by AuthStore
  }

  onLogoutSuccess(): void {
    this.#router.navigate(['/login']);
  }

  onError(error: Error): void {
    console.error('[TokenAuthProvider] Error:', error);
  }
}
